import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';

import { getAssociationById, updateAssociation, uploadAssociationLogo, deleteAssociationLogo } from 'src/shared/services/associationService';
import { getOrganizationAssociationId } from 'src/shared/functions/organizationHelpers';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { ImageCropperModal } from 'src/shared/components/organisms/imageCropperModal';
import { dataUrlToFile } from 'src/shared/functions/dataUrlToFile';
import AxiosInstance from 'src/shared/services/axios/interceptor';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { AssociationRequest } from '../models/associationRequest';
import { AssociationResponse } from '../models/associationResponse';

export const AssocationEditPage = () => {
  const { t } = useTranslation(['association']);
  const [loading, setLoading] = useState<boolean>(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { currentUser } = useGlobals();
  const params = useParams();
  const currentAssocationId = getOrganizationAssociationId(currentUser, params.id);

  const isMounted = useIsMounted();

  const [, setAssciation] = useState<AssociationResponse>();

  const requiredMessage = t('common:validationMessages.required');

  const AssociationRequestSchema: Schema<AssociationRequest> = yup.object().shape({
    localAss: yup.string().required(requiredMessage),
    nr: yup.string().optional().nullable(),
    shortName: yup.string().optional().nullable(),
    cOaddress: yup.string().optional().nullable(),
    address: yup.string().optional().nullable(),
    addressLocal: yup.string().optional().nullable(),
    postNr: yup.string().optional().nullable(),
    postAddress: yup.string().optional().nullable(),
    homesite: yup.string().optional().nullable(),
    phone: yup.string().optional().nullable(),
    phoneOther: yup.string().optional().nullable(),
    email: yup.string().optional().nullable(),
    emailResponsible: yup.string().optional().nullable(),
    fee: yup.string().optional().nullable(),
    bankGiro: yup.string().optional().nullable(),
    postGiro: yup.string().optional().nullable(),
    orgNr: yup.string().optional().nullable(),
    customerNr: yup.string().optional().nullable(),
    comment: yup.string().optional().nullable(),
  });

  const formMethods = useForm<AssociationRequest>({
    resolver: yupResolver(AssociationRequestSchema),
    mode: 'all',
    defaultValues: {
      localAss: '',
      nr: '',
      shortName: '',
      cOaddress: '',
      address: '',
      addressLocal: '',
      postNr: '',
      postAddress: '',
      homesite: '',
      phone: '',
      phoneOther: '',
      email: '',
      emailResponsible: '',
      fee: '',
      bankGiro: '',
      postGiro: '',
      orgNr: '',
      customerNr: '',
      comment: '',
    },
  });

  const { handleSubmit, trigger, reset } = formMethods;

  const saveFormData = useCallback(
    async (data: AssociationRequest) => {
      setLoading(true);
      try {
        const res = await updateAssociation(data);
        if (isMounted()) {
          ConsoleHelper.log('res updateAssociation', res);
          toast.success(t('association:saveSuccess') as string);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, t],
  );
  const onSubmit = async (data: AssociationRequest) => {
    ConsoleHelper.log('onSubmit', data);
    await trigger();
    await saveFormData(data);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getAssociationById(currentAssocationId);
        if (isMounted()) {
          setAssciation(result.data);
          reset(result.data);
          if (result.data.logoImage_id > 0) {
            try {
              const logoRes = await AxiosInstance.get(`Association/${result.data.localAss_id}/Logo`, { responseType: 'blob' });
              if (isMounted()) setLogoUrl(URL.createObjectURL(logoRes.data));
            } catch {
              // Ingen logga
            }
          }
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };

    fetchData();
  }, [isMounted, currentAssocationId, reset]);

  const { associationName } = useDistrictPickerHeader();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedImage(URL.createObjectURL(file));
    setCropperOpen(true);
    e.target.value = '';
  };

  const handleCropComplete = async (croppedDataUrlOrPromise: string | Promise<string | null> | null) => {
    setCropperOpen(false);
    const croppedDataUrl = croppedDataUrlOrPromise instanceof Promise ? await croppedDataUrlOrPromise : croppedDataUrlOrPromise;
    if (!croppedDataUrl || !currentAssocationId) return;

    const file = dataUrlToFile(croppedDataUrl, 'logo.png');
    if (!file) return;

    setUploading(true);
    try {
      await uploadAssociationLogo(Number(currentAssocationId), file);
      const logoRes = await AxiosInstance.get(`Association/${currentAssocationId}/Logo`, { responseType: 'blob' });
      setLogoUrl(URL.createObjectURL(logoRes.data));
      toast.success(t('association:logoUploaded') as string);
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('common:error.general') as string);
    } finally {
      if (isMounted()) setUploading(false);
    }
  };

  return (
    <FormLoaderWrapper loading={loading}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Grid spacing={3} container>
              <Grid item xs={12} lg={7}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {associationName}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={12} sm={4}>
                    <ControlledTextField label={t('association:nr')} name='nr' />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <ControlledTextField label={t('association:name')} required name='localAss' />
                  </Grid>
                </Grid>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
                  {t('association:other')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('association:postGiro')} name='postGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('association:bankGiro')} name='bankGiro' />
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <ControlledTextField label={t('association:orgNr')} name='orgNr' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('association:comment')} multiline rows={4} name='comment' />
                  </Grid>
                </Grid>
                {/* Logotyp */}
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
                  {t('association:logo')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 150,
                      height: 55,
                      border: 2,
                      borderStyle: logoUrl ? 'solid' : 'dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {logoUrl ? (
                      <Box component='img' src={logoUrl} alt='Logo' sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }} />
                    ) : (
                      <CloudUploadIcon sx={{ fontSize: 36, color: 'grey.400' }} />
                    )}
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant='outlined'
                        size='small'
                        startIcon={<CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {logoUrl ? t('association:changeLogo') : t('association:uploadLogo')}
                      </Button>
                      {logoUrl && (
                        <Button
                          variant='outlined'
                          size='small'
                          color='error'
                          startIcon={<DeleteIcon />}
                          onClick={async () => {
                            if (!currentAssocationId) return;
                            try {
                              await deleteAssociationLogo(Number(currentAssocationId));
                              setLogoUrl(null);
                            } catch (e) {
                              ConsoleHelper.log(e);
                            }
                          }}
                          disabled={uploading}
                        >
                          {t('common:button.delete')}
                        </Button>
                      )}
                    </Box>
                    <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, lineHeight: 1.3, display: 'block' }}>
                      {t('association:logoHint')}
                    </Typography>
                  </Box>
                  <input ref={fileInputRef} type='file' accept='image/png,image/jpeg' style={{ display: 'none' }} onChange={handleFileSelect} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Typography variant='body1' sx={{ fontWeight: 600, mb: 2 }}>
                  {t('association:contact')}
                </Typography>
                <Grid spacing={2} container>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:address')} name='address' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:coAddress')} name='cOaddress' />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField label={t('association:postNr')} name='postNr' />
                  </Grid>
                  <Grid item xs={8}>
                    <ControlledTextField label={t('association:postAddress')} name='postAddress' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('association:addressLocal')} name='addressLocal' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:email')} name='email' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:emailResponsible')} name='emailResponsible' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:phone')} name='phone' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField label={t('association:phoneOther')} name='phoneOther' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField label={t('association:homesite')} name='homesite' />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type='submit' variant='contained' disableElevation>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
        {/* Cropper-modal */}
        {selectedImage && (
          <ImageCropperModal
            open={cropperOpen}
            image={selectedImage}
            onComplete={handleCropComplete}
            onAbort={() => setCropperOpen(false)}
            containerStyle={{}}
            outputFormat='image/png'
            aspect={3 / 1}
          />
        )}
      </FormProvider>
    </FormLoaderWrapper>
  );
};
